import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { Reveal } from "./Reveal";
import { Motif } from "./Motif";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const field =
  "w-full rounded-2xl bg-white/95 border border-transparent px-5 py-3.5 text-sm text-kern-dark placeholder:text-kern-taupe/60 focus:outline-none focus:border-kern-terracotta/50 focus:ring-2 focus:ring-kern-terracotta/20 transition-colors duration-300";

export const ContactCTA = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Vul je naam, e-mail en bericht in.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      toast.success(data.message || "Bedankt! Ik neem snel contact met je op.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Er ging iets mis. Probeer het later opnieuw.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="max-w-7xl mx-auto px-5 md:px-8 pb-16 md:pb-32">
      <div className="relative overflow-hidden rounded-[48px] bg-kern-olive text-kern-bg">
        <div className="pointer-events-none absolute -right-20 -bottom-24 w-[420px] h-[420px]">
          <Motif opacity={0.16} />
        </div>
        <div className="pointer-events-none absolute -left-24 -top-24 w-[300px] h-[300px]">
          <Motif opacity={0.1} flip />
        </div>

        <div className="relative grid lg:grid-cols-2 gap-10 lg:gap-16 p-6 md:p-14 lg:p-16 items-center">
          <Reveal>
            <span className="text-xs tracking-[0.28em] uppercase text-kern-sage">Contact</span>
            <h2 className="mt-5 font-serif text-3xl md:text-5xl leading-tight tracking-tight">
              Wil je graag een afspraak maken of heb je verdere vragen, stuur me
              vrijblijvend een mailtje.
            </h2>
            <p className="mt-5 max-w-md text-kern-bg/80 leading-relaxed">
              Telefonisch ben ik niet op elk moment bereikbaar. Stuur gerust een
              bericht of laat je gegevens na op het antwoordapparaat en ik neem zo
              spoedig mogelijk contact met je op.
            </p>
            <div className="mt-9 space-y-2 text-sm text-kern-bg/85">
              <p>E. Van Notenlaan 25, 2960 Sint-Lenaarts</p>
              <p>info@praktijk-dekern.be</p>
              <p>0496 / 43 49 95</p>
              <p>Ondernemingsnummer: 1038.830.507</p>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <form onSubmit={submit} data-testid="contact-form" className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="contact-name" className={field} placeholder="Naam *" value={form.name} onChange={set("name")} />
                <input data-testid="contact-email" type="email" className={field} placeholder="E-mail *" value={form.email} onChange={set("email")} />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="contact-phone" className={field} placeholder="Telefoon" value={form.phone} onChange={set("phone")} />
                <input data-testid="contact-subject" className={field} placeholder="Onderwerp" value={form.subject} onChange={set("subject")} />
              </div>
              <textarea data-testid="contact-message" rows={4} className={`${field} resize-none`} placeholder="Je bericht *" value={form.message} onChange={set("message")} />
              <motion.button
                data-testid="contact-submit"
                type="submit"
                disabled={loading}
                whileHover={{ scale: loading ? 1 : 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-kern-terracotta text-kern-bg px-7 py-3.5 text-sm font-medium hover:bg-[#b98771] transition-colors duration-300 disabled:opacity-70"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                {loading ? "Versturen..." : "Neem contact op"}
              </motion.button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
